import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../users/userform.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiArrowLeft } from "react-icons/fi";
import InternalFooter from "../components/InternalFooter/InternalFooter";
import DateInput from "../components/Forms/DateInput";
import { createVehicleRegistration, updateVehicleRegistration, getVehicleRegistrations } from "../../services/VehicleService";

const VehicleRegistrationForm = () => {
  const navigate = useNavigate();
  const { vehicleId, registrationId } = useParams();
  const isEditMode = !!registrationId;

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [dataLoaded, setDataLoaded] = useState(!isEditMode);

  const [formData, setFormData] = useState({
    registration_start_date: "",
    registration_end_date: "",
    registered_number: ""
  });

  const notifySuccess = (msg) => toast.success(`✔️ ${msg}`, { position: 'top-right', autoClose: 3000 });
  const notifyError = (msg) => toast.error(`❌ ${msg}`, { position: 'top-right', autoClose: 5000 });

  useEffect(() => {
    if (isEditMode) {
      fetchRegistrationData();
    }
  }, [vehicleId, registrationId]);

  const fetchRegistrationData = async () => {
    try {
      setLoading(true);
      const res = await getVehicleRegistrations(vehicleId);
      const registrations = res.data.data || [];
      const registration = registrations.find(reg => reg.id === parseInt(registrationId));
      
      if (registration) {
        setFormData({
          registration_start_date: registration.registration_start_date ? new Date(registration.registration_start_date).toISOString().split('T')[0] : "",
          registration_end_date: registration.registration_end_date ? new Date(registration.registration_end_date).toISOString().split('T')[0] : "",
          registered_number: registration.registered_number || ""
        });
      } else {
        notifyError("Registration not found");
        navigate(`/vehicle/vehicleDetails/${vehicleId}`);
      }
      setDataLoaded(true);
    } catch (error) {
      console.error("Registration fetch error:", error);
      notifyError(error?.response?.data?.message || "Failed to load registration data");
      setDataLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const errs = {};
    if (!formData.registration_start_date) {
      errs.registration_start_date = "Start date is required";
    }
    if (!formData.registration_end_date) {
      errs.registration_end_date = "End date is required";
    }
    if (formData.registration_start_date && formData.registration_end_date) {
      const start = new Date(formData.registration_start_date);
      const end = new Date(formData.registration_end_date);
      if (end <= start) {
        errs.registration_end_date = "End date must be after start date";
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      notifyError("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    setLoading(true);
    try {
      const payload = {
        registration_start_date: formData.registration_start_date,
        registration_end_date: formData.registration_end_date,
        registered_number: formData.registered_number || null
      };

      if (isEditMode) {
        await updateVehicleRegistration(vehicleId, registrationId, payload);
        notifySuccess("Registration updated successfully");
      } else {
        await createVehicleRegistration(vehicleId, payload);
        notifySuccess("Registration added successfully");
      }
      setTimeout(() => navigate(`/vehicle/vehicleDetails/${vehicleId}`), 1500);
    } catch (error) {
      console.error("Submit error:", error);
      notifyError(error?.response?.data?.message || error?.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'add'} registration`);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  if (!dataLoaded && loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button
        className="back-button"
        onClick={() => navigate(`/vehicle/vehicleDetails/${vehicleId}`)}
      >
        <FiArrowLeft style={{ marginRight: "0.5rem" }} />
        Back
      </button>

      <ToastContainer />

      <div className="card p-3 p-md-4 mt-2">
        <h4 className="mb-4">{isEditMode ? "Edit Registration" : "Add Registration"}</h4>
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-12 col-md-6 mb-3">
              <label>Registration Start Date*</label>
              <DateInput
                type="date"
                name="registration_start_date"
                value={formData.registration_start_date}
                onChange={handleChange}
                className={`form-control ${errors.registration_start_date ? 'is-invalid' : ''}`}
                required
              />
              {errors.registration_start_date && <div className="text-danger small">{errors.registration_start_date}</div>}
            </div>

            <div className="col-12 col-md-6 mb-3">
              <label>Registration End Date*</label>
              <DateInput
                type="date"
                name="registration_end_date"
                value={formData.registration_end_date}
                onChange={handleChange}
                className={`form-control ${errors.registration_end_date ? 'is-invalid' : ''}`}
                required
              />
              {errors.registration_end_date && <div className="text-danger small">{errors.registration_end_date}</div>}
            </div>

            <div className="col-12 mb-3">
              <label>Registered Number</label>
              <input
                type="text"
                name="registered_number"
                value={formData.registered_number}
                onChange={handleChange}
                className="form-control"
                placeholder="Enter registered number"
              />
            </div>
          </div>

          <div className="toolbar toolbar-bottom p-2">
            <div className="d-flex flex-row justify-content-end gap-2 w-100">
              <button
                type="button"
                className="btn btn-cancel"
                onClick={() => navigate(`/vehicle/vehicleDetails/${vehicleId}`)}
                disabled={isSubmitting}
                style={{ flex: 1, maxWidth: '200px', minWidth: '100px' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn user-form-btn sw-btn-next"
                style={{ flex: 1, maxWidth: '200px', minWidth: '100px' }}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                ) : null}
                {isEditMode ? "Update" : "Submit"}
              </button>
            </div>
          </div>
        </form>
      </div>

      <InternalFooter />
    </div>
  );
};

export default VehicleRegistrationForm;
